// @ts-check

import { registerPlugin } from '@mailonline/dmg-ads-core/src/plugin-registry'
import { repeatUntil } from '@mailonline/dmg-ads-core/src/ads-utils/Async'

registerPlugin('overlay-detector', {
  ccpa: { enabledIfDissent: true },
  gdpr: { enabledIfDissent: true },
  async run({ broker, getConfig, doc, log, win }) {
    log('waiting for outbrain to appear')
    await broker.until('outbrain render')

    const getOutbrainElement = () => doc.querySelector(".outbrain-feed, ad-slot[pos='chumbox']")
    let outbrainElement = /** @type {HTMLElement} */(getOutbrainElement())
    if (!outbrainElement) {
      outbrainElement = await repeatUntil(getOutbrainElement)
    }

    const {
      monitoringInterval = 1_000,
      gridSpacePixels = 10,
      ignoreSelectorRegex = null,
      ignoreSelectors = [
        'nav.site-navigation.site-navigation--expanded'
      ]
    } = getConfig('overlay-detector', /** @type {*} */({}))

    const ignoreSelectorMatcher = ignoreSelectorRegex ? new RegExp(ignoreSelectorRegex) : null

    const elementsWeDontCareAbout = ignoreSelectors
      .map(selector => doc.querySelector(selector))
      .filter(Boolean)

    /** @type {any} */
    let interval
    const intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          log('running', { monitoringInterval })
          interval = setInterval(checkForAnOverlay, monitoringInterval)
        } else {
          log('stopping')
          interval = clearInterval(interval)
        }
      }
    })

    intersectionObserver.observe(outbrainElement)

    broker.register('call for page-unload', () => ({ amountOfOverlaysDetected: detections.size }))

    /** @type {Set<string>} */
    const detections = new Set()

    function checkForAnOverlay() {
      const boundingRect = outbrainElement.getBoundingClientRect()

      const yStart = boundingRect.top < 0 ? 0 : boundingRect.top
      const yEnd = boundingRect.bottom > win.innerHeight
        ? win.innerHeight
        : boundingRect.top + boundingRect.bottom

      const xStart = boundingRect.left < 0 ? 0 : boundingRect.left
      const xEnd = boundingRect.right > win.innerWidth
        ? win.innerWidth
        : boundingRect.left + boundingRect.right

      for (let x = xStart; x <= xEnd; x += gridSpacePixels) {
        for (let y = yStart; y <= yEnd; y += gridSpacePixels) {
          const element = /** @type {HTMLElement} */(doc.elementFromPoint(win.innerWidth / 2, y))
          if (
            !element ||
            element.contains(outbrainElement) ||
            elementsWeDontCareAbout.some(e => e?.contains(element) || e === element)
          ) continue

          const isInOutbrain = outbrainElement.contains(element)

          if (!isInOutbrain) {
            const elementSelector = generateQuerySelector(element)
            if (detections.has(elementSelector)) continue

            if (ignoreSelectorMatcher?.exec(elementSelector)) {
              log('ignoring', elementSelector, 'as it matches the ignore regex')
              continue
            }

            detections.add(elementSelector)
            log.error({ y, boundingRect, element, isInOutbrain, yStart, yEnd, elementSelector })
            broker.emit('send event to bidmax', {
              type: 'custom',
              payload: {
                module: 'overlay-detector',
                event: 'overlay detected',
                elementSelector
              }
            })
          }
        }
      }
    }
  }
})

/**
 * @param {HTMLElement} el
 * @returns {string}
 */
function generateQuerySelector(el, selector = '') {
  if (el.tagName.toLowerCase() === 'html') { return 'HTML' }

  let str = el.tagName
  str += (el.id !== '') ? '#' + el.id : ''

  if (el.className) {
    const classes = el.className.split(/\s/)
    for (let i = 0; i < classes.length; i++) {
      str += '.' + classes[i]
    }
  }

  if (el.id) return str

  str = selector ? `${str} > ${selector}` : ''

  return el.parentElement ? generateQuerySelector(el.parentElement, str) : selector
}